import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import omnigoSdk from '../../../../common/services/sdk';
import StartListBody from '../../../../common/components/startListBody';
import { getStartListProvider } from '../../../../common/services/startListProviders';
import { useStartList } from '../../../../common/hooks/useStartList';

// Deliberately the same copy for every configuration and lookup failure.
// Telling a stranger's visitors whether an event is unknown or merely not set
// up leaks internal state to no one who can act on it; the console carries the
// distinction for whoever can.
const UNAVAILABLE = 'Start list unavailable.';

const Message = ({ children }) => (
    <Typography variant="body2" color="text.secondary">{children}</Typography>
);

// Fetches the event so its `options` can drive the start list. Separate from
// `useStartList` because the widget starts from a URL slug rather than an
// already-loaded event.
const useEventOptions = (urlPath, providerKey, fetchEvent) => {
    const [options, setOptions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let current = true;

        if (!urlPath || !providerKey) {
            setLoading(false);
            return () => { current = false; };
        }

        setLoading(true);
        setFailed(false);

        fetchEvent(`events/${urlPath}`)
            .then(event => {
                if (!current) {
                    return;
                }
                setOptions(event?.options?.[providerKey] ?? null);
                setLoading(false);
            })
            .catch(ex => {
                if (!current) {
                    return;
                }
                console.warn(`omnigo: could not load event ${JSON.stringify(urlPath)}`, ex);
                setFailed(true);
                setLoading(false);
            });

        return () => { current = false; };
    }, [urlPath, providerKey]);

    return { options, loading, failed };
};

/**
 * Start list for one event, driven by `data-event` and `data-reg-provider`.
 *
 * Renders the same body as the website's start-list pages, minus the page
 * chrome -- the host page supplies its own heading.
 */
const StartList = ({ event: urlPath, regProvider, fetchPath = omnigoSdk.get }) => {
    const provider = getStartListProvider(regProvider);

    if (!urlPath) {
        console.warn('omnigo: start-list widget needs a data-event attribute.');
        return <Message>{UNAVAILABLE}</Message>;
    }

    if (!provider) {
        console.warn(
            `omnigo: unrecognised data-reg-provider ${JSON.stringify(regProvider)}.`,
            'Supported: bikereg, runsignup.'
        );
        return <Message>{UNAVAILABLE}</Message>;
    }

    return <ConfiguredStartList urlPath={urlPath} provider={provider} fetchPath={fetchPath} />;
};

// Split out because the hooks below must not sit behind the guards above.
const ConfiguredStartList = ({ urlPath, provider, fetchPath }) => {
    const { options, loading: eventLoading, failed } = useEventOptions(urlPath, provider.key, fetchPath);
    const { records, loading: entriesLoading, error } = useStartList(
        eventLoading ? null : provider,
        options,
        fetchPath
    );

    if (failed) {
        return <Message>{UNAVAILABLE}</Message>;
    }

    // An event that loaded but carries no config for this provider is set up
    // wrong, not broken.
    if (!eventLoading && !provider.isConfigured(options)) {
        console.warn(
            `omnigo: event ${JSON.stringify(urlPath)} has no ${provider.key} start-list configuration.`
        );
        return <Message>{UNAVAILABLE}</Message>;
    }

    return (
        <StartListBody
            provider={provider}
            startList={options?.startList}
            records={records}
            loading={eventLoading || entriesLoading}
            error={error}
        />
    );
};

export default StartList;
